/**
 * The webhook's contract, two ways: the table a person fills in (which
 * params, what type, required or not) and the request it adds up to — URL
 * with its path params, required query and headers, an example body, and a
 * curl line to paste. The preview is what a caller needs to know; the
 * router enforces the same contract, so what is shown here is what is true.
 */

import { useMemo, useState } from 'react'
import { CopyButton } from '@/shared/components'
import type { WfNode } from '../types'

export interface ContractField {
  name: string
  type: 'string' | 'number' | 'integer' | 'boolean' | 'json'
  required: boolean
  description: string
  example: string
}

const TYPES: ContractField['type'][] = ['string', 'number', 'integer', 'boolean', 'json']

export function contractFields(raw: unknown): ContractField[] {
  return ((raw as Partial<ContractField>[] | undefined) ?? []).map((f) => ({
    name: String(f.name ?? ''),
    type: TYPES.includes(f.type as ContractField['type']) ? (f.type as ContractField['type']) : 'string',
    required: Boolean(f.required),
    description: String(f.description ?? ''),
    example: f.example === undefined || f.example === null ? '' : String(f.example),
  }))
}

/** The editable table for query params or headers. */
export function ContractTable({ value, onChange, kind }: { value: unknown; onChange: (v: ContractField[]) => void; kind: 'query' | 'headers' }) {
  const rows = contractFields(value)
  const set = (i: number, patch: Partial<ContractField>) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  return (
    <div className="wf-contract">
      {rows.length > 0 && (
        <div className="wf-contract-head small muted">
          <span>Name</span>
          <span>Type</span>
          <span>Req.</span>
          <span>Example</span>
          <span />
        </div>
      )}
      {rows.map((r, i) => (
        <div className="wf-contract-row" key={i}>
          <input className="input input-mono" value={r.name} placeholder={kind === 'headers' ? 'X-Tenant-Id' : 'limit'} aria-label="Name" onChange={(e) => set(i, { name: e.target.value })} />
          <select className="select" value={r.type} aria-label="Type" onChange={(e) => set(i, { type: e.target.value as ContractField['type'] })}>
            {TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <label className="wf-contract-req" title="Required">
            <input type="checkbox" checked={r.required} aria-label="Required" onChange={(e) => set(i, { required: e.target.checked })} />
          </label>
          <input className="input" value={r.example} placeholder="example" aria-label="Example" onChange={(e) => set(i, { example: e.target.value })} />
          <button type="button" className="btn btn-ghost btn-icon btn-sm" aria-label="Remove" onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>×</button>
          <input className="input wf-contract-desc" value={r.description} placeholder="What it is for (shown to callers)" aria-label="Description" onChange={(e) => set(i, { description: e.target.value })} />
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...rows, { name: '', type: 'string', required: false, description: '', example: '' }])}>
        + Add {kind === 'headers' ? 'header' : 'parameter'}
      </button>
    </div>
  )
}

// --- the preview -------------------------------------------------------------------------------

function exampleFor(f: ContractField): unknown {
  if (f.example !== '') {
    if (f.type === 'number' || f.type === 'integer') return Number(f.example)
    if (f.type === 'boolean') return f.example === 'true'
    if (f.type === 'json') {
      try {
        return JSON.parse(f.example)
      } catch {
        return f.example
      }
    }
    return f.example
  }
  return { string: 'value', number: 1.5, integer: 1, boolean: true, json: {} }[f.type]
}

export function exampleBody(schema: unknown): unknown {
  if (!schema || typeof schema !== 'object') return {}
  const s = schema as Record<string, unknown>
  if ('example' in s) return s.example
  if (Array.isArray(s.examples) && s.examples.length) return s.examples[0]
  if ('default' in s) return s.default
  if (Array.isArray(s.enum) && s.enum.length) return s.enum[0]
  const t = Array.isArray(s.type) ? (s.type as string[]).find((x) => x !== 'null') : (s.type as string | undefined)
  if (t === 'object' || s.properties) return Object.fromEntries(Object.entries((s.properties as Record<string, unknown>) ?? {}).map(([k, v]) => [k, exampleBody(v)]))
  if (t === 'array') return [exampleBody(s.items ?? {})]
  return { string: 'text', number: 1.5, integer: 1, boolean: true, null: null }[t ?? 'string'] ?? 'text'
}

export function parseSchema(raw: unknown): Record<string, unknown> | null {
  if (!raw) return null
  if (typeof raw === 'object') return raw as Record<string, unknown>
  try {
    const v = JSON.parse(String(raw))
    return v && typeof v === 'object' ? v : null
  } catch {
    return null
  }
}

export function WebhookPreview({ node, workflowActive }: { node: WfNode; workflowActive: boolean }) {
  const [tab, setTab] = useState<'request' | 'curl'>('request')
  const path = String(node.params.path ?? '').replace(/^\/+|\/+$/g, '')
  const method = String(node.params.method ?? 'POST').toUpperCase()
  const respond = String(node.params.respond ?? 'immediately')
  const query = contractFields(node.params.query)
  const headers = contractFields(node.params.headers)
  const schema = parseSchema(node.params.body_schema)
  const secret = String(node.params.secret ?? '')
  const pathParams = useMemo(() => [...path.matchAll(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g)].map((m) => m[1]), [path])
  const hasBody = !['GET', 'DELETE'].includes(method === 'ANY' ? 'POST' : method)

  const base = `${window.location.origin}/api/v1/workflows/hooks/`
  const concretePath = path.replace(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g, (_, n) => `<${n}>`)
  const qs = query.length ? `?${query.map((q) => `${encodeURIComponent(q.name || 'name')}=${encodeURIComponent(String(exampleFor(q)))}`).join('&')}` : ''
  const url = `${base}${concretePath}${qs}`
  const body = hasBody ? exampleBody(schema) : null

  const curl = [
    `curl -X ${method === 'ANY' ? 'POST' : method} '${url}'`,
    ...headers.map((h) => `  -H '${h.name || 'X-Header'}: ${String(exampleFor(h))}'`),
    ...(secret ? [`  -H 'X-Webhook-Secret: ${secret}'`] : []),
    ...(hasBody ? [`  -H 'Content-Type: application/json'`, `  -d '${JSON.stringify(body)}'`] : []),
  ].join(' \\\n')

  const responseText = {
    immediately: '202 Accepted at once, with { "run_id" } — the run continues in the background',
    when_done: '200 OK when the run finishes, with what the workflow responds (a Respond node sets the body and status)',
    no_content: '204 No Content at once, no body — the run continues in the background (run id in X-Run-Id)',
  }[respond] ?? respond

  return (
    <div className="wf-hook">
      <div className="wf-hook-head">
        <span className="wf-hook-method" data-method={method}>{method}</span>
        <code className="wf-hook-url">
          {base}
          {path.split(/(\{[^}]+\})/).map((part, i) => (part.startsWith('{') ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>))}
          {!path && <span className="muted">…</span>}
        </code>
        <CopyButton value={`${base}${path}`} />
      </div>
      {!workflowActive && <div className="small muted wf-hook-note">Not live yet — switch the workflow on for this URL to answer.</div>}

      <div className="wf-tabs wf-tabs-inline">
        <button type="button" data-on={tab === 'request'} onClick={() => setTab('request')}>What to send</button>
        <button type="button" data-on={tab === 'curl'} onClick={() => setTab('curl')}>curl</button>
      </div>

      {tab === 'request' ? (
        <dl className="wf-hook-spec">
          {pathParams.length > 0 && (
            <>
              <dt>Path</dt>
              <dd>{pathParams.map((p) => <code key={p} className="wf-hook-chip">{`{${p}}`}</code>)}</dd>
            </>
          )}
          {query.length > 0 && (
            <>
              <dt>Query</dt>
              <dd>
                {query.map((q, i) => (
                  <div key={i} className="wf-hook-field">
                    <code>{q.name || '?'}</code>
                    <span className="wf-datapicker-type" data-type={q.type}>{q.type}</span>
                    {q.required && <span className="wf-hook-req">required</span>}
                    {q.description && <span className="muted">{q.description}</span>}
                  </div>
                ))}
              </dd>
            </>
          )}
          {(headers.length > 0 || secret) && (
            <>
              <dt>Headers</dt>
              <dd>
                {headers.map((h, i) => (
                  <div key={i} className="wf-hook-field">
                    <code>{h.name || '?'}</code>
                    <span className="wf-datapicker-type" data-type={h.type}>{h.type}</span>
                    {h.required && <span className="wf-hook-req">required</span>}
                    {h.description && <span className="muted">{h.description}</span>}
                  </div>
                ))}
                {secret && (
                  <div className="wf-hook-field">
                    <code>X-Webhook-Secret</code>
                    <span className="wf-hook-req">required</span>
                    <span className="muted">or ?secret=</span>
                  </div>
                )}
              </dd>
            </>
          )}
          {hasBody && (
            <>
              <dt>Body</dt>
              <dd>
                {schema ? <pre className="json wf-json wf-hook-body">{JSON.stringify(body, null, 2)}</pre> : <span className="muted small">Any JSON (or text). Add a body schema to document and enforce it.</span>}
              </dd>
            </>
          )}
          <dt>Reply</dt>
          <dd className="small">{responseText}</dd>
          <dt>Inside</dt>
          <dd className="small muted wf-hook-inline">
            The first item is the request: <code>$json.params</code>, <code>$json.query</code>, <code>$json.headers</code>, <code>$json.body</code>. Requests that break this contract get a 400 with the problems listed and never start a run.
          </dd>
        </dl>
      ) : (
        <div className="wf-hook-curl">
          <pre className="json wf-json">{curl}</pre>
          <CopyButton value={curl} label="Copy curl" />
        </div>
      )}
    </div>
  )
}
